import classNames from '@utils/classnames';
import React from 'react';
import { Link } from 'react-router-dom';

export interface ILinkCardProps {
  title: string;
  content?: React.ReactNode;
  to: string;
}

/** Card that navigates on click. */
export const LinkCard: React.FC<ILinkCardProps> = (props) => {
  const { title, content, to } = props;

  return (
    <Link to={to} className="block">
      <div className="card p-4" data-clickable="true">
        <div className="card-heading-text">{title}</div>
        {content && <div className="mt-2 text-sm text-fg-secondary">{content}</div>}
      </div>
    </Link>
  );
};

export interface ICardHeadingProps {
  title: React.ReactNode;
  /** Optional muted line under the title, e.g. a short rule for the form below. */
  subtitle?: React.ReactNode;
  rightHeading?: React.ReactNode;
}

export interface ICardProps extends ICardHeadingProps {
  children: React.ReactNode;
  onClick?: () => void;
  className?: string;
  /** Remove the body padding, e.g. when the body is a table or list. */
  flush?: boolean;
}

/**
 * The sunken title bar of a panel. `Card` draws it inside its frame; a panel that cannot be a card —
 * one holding cards of its own — draws it on its own, so the two still read as the same bar.
 */
export const CardHeading: React.FC<ICardHeadingProps> = ({ title, subtitle, rightHeading }) => {
  return (
    <div className="card-heading">
      <div className="min-w-0">
        <div className="truncate">{title}</div>
        {subtitle != null && <div className="card-subtle-text mt-0.5 font-normal">{subtitle}</div>}
      </div>
      {rightHeading != null && <div className="flex shrink-0 items-center gap-2">{rightHeading}</div>}
    </div>
  );
};

/**
 * Panel with a titled header — white surface, 1px border, radius 8, no shadow.
 * Never nest a card inside a card.
 */
export const Card: React.FC<ICardProps> = (props) => {
  const { title, subtitle, rightHeading, children, onClick, className, flush } = props;

  return (
    <div className={classNames('card', className)} onClick={onClick} data-clickable={Boolean(onClick)}>
      <CardHeading title={title} subtitle={subtitle} rightHeading={rightHeading} />
      {/* A flush body is rendered without a wrapper, so a table or list inside it
          is a direct child of `.card` and the flush styling applies to it. */}
      {flush ? children : <div className="card-content">{children}</div>}
    </div>
  );
};
